<script setup lang="ts">
import AppLayout from '../layouts/AppLayout.vue'
import { computed, onMounted, ref } from 'vue'
import { Pencil } from 'lucide-vue-next'
import { useAuth } from '../composables/useAuth'
import { getSupplierProfile } from '../services/api'
import { API_CONFIG } from '../config/api'
import type { SupplierProfile } from '../types/supplier'
import { useCurrentContext } from '../composables/useCurrentContext'
import { useUserStore } from '../stores/user'
import ProfileHeader, { type ProfileTab } from '../components/supplier/company-profile/ProfileHeader.vue'
import ClampText from '../components/supplier/company-profile/ClampText.vue'
import SectionHead from '../components/supplier/company-profile/SectionHead.vue'
import ProductsSection from '../components/supplier/company-profile/ProductsSection.vue'
import TrainingSection from '../components/supplier/company-profile/TrainingSection.vue'
import DocumentsSection, { type ProfileDoc } from '../components/supplier/company-profile/DocumentsSection.vue'
import ProfileRail from '../components/supplier/company-profile/ProfileRail.vue'
import CopyableText from '../components/CopyableText.vue'
import type { ProfileProduct } from '../components/supplier/company-profile/productList'

const { supplierId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()
const userStore = useUserStore()

const loading = ref(true)
const profile = ref<SupplierProfile | null>(null)
const error = ref<string | null>(null)
const docs = ref<ProfileDoc[]>([])
const tab = ref<ProfileTab>('overview')

// Edit rights come from the store so every supplier page agrees with the backend tier gate.
const canEditProfile = computed(() => userStore.canEditSupplierProfile)
const canEditProducts = computed(() => userStore.canEditSupplierProducts)

// Same endpoint SupplierDocumentsCard uses in manage mode; a failure just shows 0 documents.
async function loadDocs() {
  if (!supplierId.value) return
  try {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/documents`, { headers: { Authorization: `Bearer ${token}` } })
    if (res.ok) docs.value = await res.json()
  } catch (e) { console.error('Documents load failed', e) }
}

onMounted(async () => {
  try {
    if (!supplierId.value) throw new Error('No supplier context')
    const token = await getAccessTokenSilently()
    const [p] = await Promise.all([getSupplierProfile(supplierId.value, token), loadDocs()])
    profile.value = p
  } catch (e) {
    console.error(e)
    error.value = 'Failed to load company profile.'
  } finally {
    loading.value = false
  }
})

const products = computed(() => (profile.value?.products || []) as ProfileProduct[])
const training = computed(() => profile.value?.trainingAssets || [])
const employees = computed(() => profile.value?.employees || [])
const counts = computed(() => ({
  products: products.value.length,
  training: training.value.length,
  documents: docs.value.length,
  contacts: employees.value.length,
}))
const show = (t: ProfileTab) => tab.value === 'overview' || tab.value === t

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
</script>

<template>
  <AppLayout>
    <div class="csp -m-4 md:-m-8 min-h-full">
      <div v-if="loading" class="flex justify-center py-20" role="status" aria-label="Loading company profile">
        <svg class="animate-spin w-8 h-8 text-(--csp-brand)" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
      </div>

      <div v-else-if="error" class="max-w-[1080px] mx-auto p-6">
        <div class="bg-red-50 text-red-700 p-4 rounded-[6px]" role="alert">{{ error }}</div>
      </div>

      <template v-else-if="profile">
        <ProfileHeader v-model:tab="tab" :name="profile.name" :logo-url="profile.logoUrl"
          :website-url="profile.websiteUrl" :counts="counts" :can-edit="canEditProfile" />

        <div class="max-w-[1080px] mx-auto px-6 py-6 flex flex-col min-[860px]:flex-row gap-6 items-start">
          <div id="csp-tabpanel" role="tabpanel" :aria-labelledby="`csp-tab-${tab}`"
            class="csp-panel csp-sheet w-full min-[860px]:flex-1 min-w-0">
            <section v-if="tab === 'overview'" aria-labelledby="csp-about">
              <SectionHead id="csp-about" title="About">
                <router-link v-if="canEditProfile" :to="{ name: 'supplier-settings' }" class="csp-link inline-flex items-center gap-1">
                  <Pencil :size="13" aria-hidden="true" /> Edit
                </router-link>
              </SectionHead>
              <ClampText :text="profile.summary" :lines="3" class="max-w-[70ch] leading-[1.6] text-(--csp-text-2)" />
              <p class="text-[13px] font-semibold text-(--csp-text) mt-4 mb-2">Solution categories</p>
              <ul v-if="profile.categories?.length" class="flex flex-wrap gap-2">
                <li v-for="c in profile.categories" :key="c" class="csp-tag">{{ c }}</li>
              </ul>
              <p v-else class="text-[13px] csp-muted">None</p>
            </section>

            <ProductsSection v-if="show('products')" :products="products" :can-edit="canEditProducts" />
            <TrainingSection v-if="show('training')" :assets="training" :can-edit="canEditProducts" :show-all="tab === 'training'" @see-all="tab = 'training'" />
            <DocumentsSection v-if="show('documents') && supplierId" :supplier-id="supplierId" :docs="docs" :can-edit="canEditProfile" @changed="loadDocs" />

            <section v-if="tab === 'contacts'" aria-labelledby="csp-all-contacts">
              <SectionHead id="csp-all-contacts" title="Channel contacts" :count="employees.length">
                <router-link v-if="canEditProfile" :to="{ name: 'supplier-contacts' }" class="csp-link inline-flex items-center gap-1"><Pencil :size="13" aria-hidden="true" /> Manage</router-link>
              </SectionHead>
              <ul>
                <li v-for="e in employees" :key="e.id" class="csp-row flex items-center gap-3 border-b border-(--csp-divider) last:border-b-0">
                  <span class="w-8 h-8 rounded-full bg-(--csp-tag) text-(--csp-text-2) text-xs font-semibold flex items-center justify-center shrink-0" aria-hidden="true">{{ initials(e.name) }}</span>
                  <div class="min-w-0 flex-1">
                    <p class="font-semibold text-(--csp-text) truncate">{{ e.name }}</p>
                    <p class="text-[13px] csp-muted truncate">{{ e.role }}</p>
                  </div>
                  <span class="flex items-center gap-1.5 max-w-[45%] min-w-0">
                    <a :href="`mailto:${e.email}`" class="csp-link text-[13px] truncate">{{ e.email }}</a>
                    <CopyableText :value="e.email" kind="email" icon-only class="csp-muted" />
                  </span>
                </li>
                <li v-if="!employees.length" class="csp-row flex items-center text-sm csp-muted">No channel contacts yet.</li>
              </ul>
            </section>
          </div>

          <ProfileRail class="w-full min-[860px]:w-[340px] min-[860px]:shrink-0" :website-url="profile.websiteUrl"
            :categories="profile.categories || []" :employees="employees" :promotions="profile.promotions || []"
            :counts="counts" :can-edit="canEditProfile" @see-all-contacts="tab = 'contacts'" />
        </div>
      </template>
    </div>
  </AppLayout>
</template>

<style>
/* Pin Tailwind's layer order. If this style tag loads before Tailwind's, `components` would be
   declared first and rank below preflight (`base`), which zeroes every padding and border here. */
@layer theme, base, components, utilities;
/* In the components layer so Tailwind utilities (e.g. pl-8) can still override these. */
@layer components {
/* Page-scoped design values for the Company Profile; everything is under .csp. */
.csp {
  --csp-bg: #F5F6F8;
  --csp-surface: #FFFFFF;
  --csp-border: #E1E2E4;
  --csp-divider: #E9EAEC;
  --csp-input: #D5D7DA;
  --csp-text: #1C2023;
  --csp-text-2: #3A3F44;
  --csp-muted: #5B5F64;
  --csp-brand: #206455;
  --csp-brand-hover: #164A40;
  --csp-on-brand: #FFFFFF;
  --csp-tag: #F1F2F4;
  --csp-logo: #0E1A2B;
  --csp-tabbar-h: 48px;
  background: var(--csp-bg);
  color: var(--csp-text);
  font-family: var(--font-sans);
}
/* Dark mode: fall back to the app's existing theme tokens. */
.dark .csp {
  --csp-bg: var(--color-cc-surface);
  --csp-surface: var(--color-cc-elevated);
  --csp-border: var(--color-cc-border);
  --csp-divider: var(--color-cc-border);
  --csp-input: var(--color-cc-border);
  --csp-text: var(--color-cc-text);
  --csp-text-2: var(--color-cc-text-secondary);
  --csp-muted: var(--color-cc-text-tertiary);
  --csp-brand: var(--color-cc-primary);
  --csp-brand-hover: var(--color-cc-primary-hover);
  --csp-on-brand: var(--color-cc-btn-text);
  --csp-tag: var(--color-cc-hover);
}
.csp .csp-surface { background: var(--csp-surface); }
.csp .csp-panel { background: var(--csp-surface); border: 1px solid var(--csp-border); border-radius: 6px; }
.csp .csp-sheet > section { padding: 24px 28px; }
.csp .csp-sheet > section + section { border-top: 1px solid var(--csp-border); }
.csp .csp-row { min-height: 56px; padding: 10px 0; }
.csp .csp-muted { color: var(--csp-muted); }
.csp .csp-link { color: var(--csp-brand); }
.csp .csp-link:hover { color: var(--csp-brand-hover); text-decoration: underline; }
.csp .csp-title-link { color: var(--csp-text); }
.csp .csp-title-link:hover { color: var(--csp-brand); }
.csp .csp-tag { font-size: 13px; padding: 2px 8px; background: var(--csp-tag); border: 1px solid var(--csp-border); border-radius: 4px; color: var(--csp-text-2); }
.csp .csp-input { height: 32px; padding: 0 8px; font-size: 13px; color: var(--csp-text); background: var(--csp-surface); border: 1px solid var(--csp-input); border-radius: 6px; }
.csp .csp-btn { display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 14px; font-size: 14px; font-weight: 500; color: var(--csp-text); background: var(--csp-surface); border: 1px solid var(--csp-input); border-radius: 6px; }
.csp .csp-btn:hover { background: var(--csp-tag); }
.csp .csp-btn-primary { color: var(--csp-on-brand); background: var(--csp-brand); border-color: var(--csp-brand); }
.csp .csp-btn-primary:hover { background: var(--csp-brand-hover); border-color: var(--csp-brand-hover); }
.csp .csp-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; color: var(--csp-text-2); border: 1px solid var(--csp-input); border-radius: 6px; }
.csp .csp-icon-btn:disabled { opacity: .4; cursor: not-allowed; }
.csp :focus-visible,
.csp label:has(> input[type=file]:focus-visible) { outline: 2px solid var(--csp-brand); outline-offset: 2px; }
.csp .csp-input:focus-visible { outline-offset: 0; }
/* Sticky tab row. AppLayout's <main> is the scroll container and the top bar + impersonation
   banner sit outside it (flex siblings), so no offset is needed for them, with or without the
   banner. Sticky is measured inside <main>'s padding (p-4 md:p-8), which this page already
   cancels with -m-4 md:-m-8, so top cancels it the same way. */
.csp .csp-tabbar { position: sticky; top: -1rem; z-index: 20; height: var(--csp-tabbar-h); background: var(--csp-surface); border-bottom: 1px solid var(--csp-border); }
@media (min-width: 768px) { .csp .csp-tabbar { top: -2rem; } }
/* The tablist scrolls horizontally, which clips an outside ring; draw it inset instead. */
.csp .csp-tab:focus-visible { outline-offset: -2px; border-radius: 4px 4px 0 0; }
/* Panel heading lands just below the stuck bar. */
.csp #csp-tabpanel { scroll-margin-top: calc(var(--csp-tabbar-h) + 16px); }
/* ClampText: "Show more" floats to the end of the last visible line. */
.csp .csp-clamp { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: var(--csp-lines); overflow: hidden; }
.csp .csp-clamp::before { content: ''; float: right; height: calc(100% - 1lh); }
.csp .csp-clamp-btn { float: right; clear: both; margin-left: 6px; line-height: inherit; }
/* Product + training rows: the title link is stretched over the whole row; hovering anywhere highlights it. */
.csp .csp-row-link { cursor: pointer; isolation: isolate; }
/* Hover fill for the whole row: bleeds 12px past the text, stops 1px short so the divider stays visible. */
.csp .csp-row-link::before { content: ''; position: absolute; inset: 0 -12px 1px; z-index: -1; border-radius: 6px; transition: background-color .15s ease; }
.csp .csp-row-link:hover::before { background: var(--csp-tag); }
.csp .csp-row-link:hover .csp-title-link { color: var(--csp-brand); }
/* Stuck-bar logo + name: slides open/closed so the tabs glide rather than jump. */
.csp .csp-mini { max-width: 0; margin-right: 0; opacity: 0; transition: max-width .25s ease, margin-right .25s ease, opacity .2s ease; }
.csp .csp-mini.is-stuck { max-width: 320px; margin-right: 16px; opacity: 1; }
@media (prefers-reduced-motion: reduce) { .csp .csp-mini { transition: none; } }
}

/* Unlayered on purpose: style.css sets `* { scrollbar-width: thin }` outside any layer, which
   would beat a layered rule. Tabs still scroll sideways by swipe / arrow keys. */
.csp .csp-tablist { scrollbar-width: none; }
.csp .csp-tablist::-webkit-scrollbar { display: none; }
</style>
